import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { FolderOpen, Plus } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import type { components } from "../api/esquema";
import { Aviso } from "../componentes/kit/Aviso";
import { Botao } from "../componentes/kit/Botao";
import { CabecalhoTela } from "../componentes/kit/CabecalhoTela";
import { Esqueleto, Falha, Vazio } from "../componentes/kit/Estados";
import { Etiqueta } from "../componentes/kit/Etiqueta";
import { LinhaDoTempo, type Origem } from "../componentes/kit/LinhaDoTempo";
import { Dados, PainelLateral } from "../componentes/kit/PainelLateral";
import { Prazo } from "../componentes/kit/Prazo";
import { usePainel } from "../componentes/kit/painel";
import { useEu } from "../sessao/eu";
import { diaDoInstante, formatarDataCurta } from "../util/datas";

type Tarefa = components["schemas"]["TarefaAberta"];

const SITUACAO_TAREFA = {
  aberta: { texto: "A fazer", tom: "neutro" },
  em_conferencia: { texto: "Em conferência", tom: "conferir" },
} as const;

/** Uma seção da página: rótulo + fio, sem caixa. */
function Secao({ titulo, contagem, children }: { titulo: string; contagem?: number; children: ReactNode }) {
  return (
    <section>
      <h2 className="flex h-8 items-center justify-between fio-b text-meta font-medium text-texto-3">
        <span>{titulo}</span>
        {contagem !== undefined && <span className="numeros font-normal">{contagem}</span>}
      </h2>
      <div className="pt-2">{children}</div>
    </section>
  );
}

/** E3-04 · Página do processo (US2, §6.4): toda tarefa abre aqui, com as demais tarefas abertas à vista. */
export function ProcessoPagina({ numero: fixo }: { numero?: string } = {}) {
  const params = useParams({ strict: false }) as { numero?: string };
  const numero = fixo ?? params.numero ?? "";
  const [aberta, setAberta] = useState<string | null>(null);
  const { data: eu } = useEu();

  const consulta = useQuery({
    queryKey: ["processo", numero],
    queryFn: () => exigir(api.GET("/api/processos/{numero}", { params: { path: { numero } } })),
    retry: (n, e) => !(e instanceof ErroApi && e.status === 404) && n < 1,
  });
  const d = consulta.data;
  const tarefas = d?.tarefas_abertas ?? [];
  const ordem = useMemo(() => new Map(tarefas.map((t, i) => [t.id, i + 1])), [tarefas]);
  const tarefa = tarefas.find((t) => t.id === aberta) ?? null;

  usePainel(useMemo(() => tarefa && d && (
    <PainelLateral titulo={tarefa.titulo} sobre={<span className="numeros">{d.numero_formatado}</span>} aoFechar={() => setAberta(null)}
      rodape={<Botao disabled title="Concluir chega com as regras de conclusão (E4-07)">Concluir tarefa</Botao>}>
      <div className="flex flex-col gap-4">
        <Etiqueta tom={SITUACAO_TAREFA[tarefa.status].tom}>{SITUACAO_TAREFA[tarefa.status].texto}</Etiqueta>
        <Dados itens={[
          { rotulo: "Prazo", valor: <Prazo execucao={tarefa.execucao} fatal={tarefa.fatal} /> },
          { rotulo: "Responsável", valor: tarefa.responsavel },
          { rotulo: "Conferência", valor: tarefa.necessaria_conferencia ? "necessária antes do protocolo" : "não precisa" },
          ...(tarefa.pontos != null ? [{ rotulo: "Pontos", valor: <span className="numeros">{tarefa.pontos}</span> }] : []),
        ]} />
        <Dependencias t={tarefa} tarefas={tarefas} ordem={ordem} />
      </div>
    </PainelLateral>
  ), [tarefa, d, tarefas, ordem]));

  if (consulta.isPending) return <Esqueleto linhas={10} />;
  if (consulta.isError) {
    const naoExiste = consulta.error instanceof ErroApi && consulta.error.status === 404;
    return naoExiste
      ? <Vazio titulo="Processo não encontrado" acao={<Link to="/processos" className="text-texto-1 underline underline-offset-2">Voltar para Processos</Link>}>Confira o número ou busque pelo nome do cliente.</Vazio>
      : <Falha mensagem={consulta.error instanceof ErroApi ? consulta.error.message : "Não foi possível abrir o processo."} aoTentar={() => consulta.refetch()} />;
  }
  if (!d) return null;

  const autores = d.clientes.filter((c) => c.papel !== "reu").map((c) => c.nome);
  const contrarios = d.partes.filter((p) => p.polo === "passivo").map((p) => p.nome);
  const titulo = [autores.join(", ") || "sem cliente", contrarios.slice(0, 3).join(", ") + (contrarios.length > 3 ? ` e mais ${contrarios.length - 3}` : "")]
    .filter(Boolean).join(" x ");
  const fatos = [d.tribunal, d.sistema, d.vara, d.fase, d.responsavel && `resp. ${d.responsavel.nome}`].filter(Boolean).join(" · ");

  return (
    <div>
      <CabecalhoTela voltar={{ para: "/processos", rotulo: "Processos" }} sobre={<span className="numeros">{d.numero_formatado}</span>}
        titulo={titulo} contexto={fatos}
        acoes={<>
          {eu?.gestor && (
            <Link to="/alteracoes" search={{ processo: d.numero }} className="inline-flex h-controle items-center rounded-controle px-3 font-medium text-texto-1 hover:bg-superficie-2">Alterações</Link>
          )}
          <Botao icone={<FolderOpen size={14} aria-hidden />} disabled title="A pasta no Drive liga com a integração do Drive (E1-06)">Pasta no Drive</Botao>
          <Link to="/lancamentos" className="inline-flex h-controle items-center gap-2 rounded-controle bg-acao px-3 font-medium text-acao-texto hover:bg-acao-hover">
            <Plus size={14} aria-hidden /> Nova tarefa
          </Link>
        </>} />
      {(d.status !== "ativo" || d.segredo_justica) && (
        <div className="-mt-2 mb-4 flex gap-2">
          {d.status !== "ativo" && <Etiqueta>{d.status[0]!.toUpperCase() + d.status.slice(1)}</Etiqueta>}
          {d.segredo_justica && <Etiqueta tom="alerta">Segredo de justiça</Etiqueta>}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-8">
          <Secao titulo="Tarefas abertas, em ordem" contagem={tarefas.length}>
            {tarefas.length === 0 ? (
              <p className="py-2 text-texto-3">Nenhuma tarefa aberta neste processo.</p>
            ) : (
              <ol className="entre-fios">
                {tarefas.map((t, i) => (
                  <li key={t.id} className={`relative py-3 hover:bg-superficie-2 ${aberta === t.id ? "bg-superficie-2" : ""}`}>
                    <div className="flex items-start gap-3">
                      <span className={`numeros grid size-6 shrink-0 place-items-center rounded-pilula text-meta font-medium ${i === 0 ? "bg-acao text-acao-texto" : "bg-superficie-2 text-texto-3"}`}>{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <button type="button" onClick={() => setAberta(t.id)} className="block w-full cursor-pointer truncate text-left font-medium text-texto-1 after:absolute after:inset-0 after:content-['']">
                          {t.titulo}
                        </button>
                        <p className="text-meta text-texto-3">
                          {t.responsavel}
                          {t.execucao && <> · exec. <span className="numeros">{formatarDataCurta(t.execucao)}</span></>}
                          {t.fatal ? <> · fatal <span className="numeros">{formatarDataCurta(t.fatal)}</span></> : " · sem fatal"}
                        </p>
                        <div className="mt-2 empty:hidden"><Dependencias t={t} tarefas={tarefas} ordem={ordem} /></div>
                      </div>
                      <Etiqueta tom={SITUACAO_TAREFA[t.status].tom}>{SITUACAO_TAREFA[t.status].texto}</Etiqueta>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Secao>

          <Secao titulo="Documentos do cliente" contagem={d.documentos.length}>
            {d.documentos.length === 0 ? (
              <p className="py-2 text-texto-3">Nenhum documento enviado pelo sistema ainda.</p>
            ) : (
              <ul className="entre-fios">
                {d.documentos.map((doc) => (
                  <li key={doc.nome_arquivo + doc.enviado_em} className="flex items-center gap-3 py-2">
                    <span className="min-w-0 flex-1 truncate">{doc.nome_arquivo}</span>
                    <span className="hidden text-meta text-texto-3 sm:block">{doc.tipo_documento}</span>
                    {doc.status === "no_drive" ? <span className="numeros text-meta text-texto-3">{diaDoInstante(doc.enviado_em)}</span>
                      : <Etiqueta tom={doc.status === "falhou" ? "alerta" : "neutro"}>{doc.status === "falhou" ? "Falhou" : "Enviando"}</Etiqueta>}
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        </div>

        <div className="flex min-w-0 flex-col gap-8">
          <Secao titulo="Partes">
            <Dados itens={[
              ...d.clientes.map((c) => ({
                rotulo: c.papel === "reu" ? "Cliente (réu)" : "Cliente",
                valor: <span className="flex flex-col"><span>{c.nome}</span><span className="text-meta text-texto-3">{[c.cpf && `CPF ${c.cpf}`, ...c.telefones].filter(Boolean).join(" · ")}</span></span>,
              })),
              ...(contrarios.length ? [{ rotulo: "Parte contrária", valor: contrarios.join(", ") }] : []),
            ]} />
          </Secao>

          {d.vinculos.length > 0 && (
            <Secao titulo="Processos vinculados">
              <ul className="flex flex-col gap-1">
                {d.vinculos.map((v) => (
                  <li key={v.numero} className="flex items-baseline gap-2">
                    <span className="text-meta text-texto-3">{v.relacao === "originario" ? "originário" : v.tipo.replace("_", " ")}</span>
                    <Link to="/processos/$numero" params={{ numero: v.numero }} className="numeros text-texto-1 underline-offset-2 hover:underline">{v.numero_formatado}</Link>
                  </li>
                ))}
              </ul>
            </Secao>
          )}

          {d.anotacoes_fixadas.length > 0 && (
            <Secao titulo="Anotações fixadas">
              <ul className="flex flex-col gap-3">
                {d.anotacoes_fixadas.map((a) => (
                  <li key={a.em + a.texto}>
                    <p className="break-words">{a.texto}</p>
                    <p className="text-meta text-texto-3">{a.autor} · <span className="numeros">{diaDoInstante(a.em)}</span></p>
                  </li>
                ))}
              </ul>
            </Secao>
          )}

          <LinhaDoTempoDoProcesso numero={numero} />
        </div>
      </div>
    </div>
  );
}

/** "Depende da tarefa 1 (Recurso). Protocolar só depois dela." Só aparece para quem pode ver a dependência. */
function Dependencias({ t, tarefas, ordem }: { t: Tarefa; tarefas: Tarefa[]; ordem: Map<string, number> }) {
  const deps = t.depende_de.map((id) => tarefas.find((x) => x.id === id)).filter((x): x is Tarefa => !!x);
  if (deps.length === 0) return null;
  const lista = deps.map((x) => `${ordem.get(x.id)} (${x.titulo})`).join(" e ");
  return <Aviso>Depende da tarefa {lista}. Protocolar só depois, para não encerrar o prazo da outra.</Aviso>;
}

function LinhaDoTempoDoProcesso({ numero }: { numero: string }) {
  const consulta = useInfiniteQuery({
    queryKey: ["processo", numero, "linha-do-tempo"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => exigir(api.GET("/api/processos/{numero}/linha-do-tempo", {
      params: { path: { numero }, query: { limite: 30, depois: pageParam ?? undefined } },
    })),
    getNextPageParam: (u) => u.proximo,
  });
  const eventos = consulta.data?.pages.flatMap((p) => p.eventos) ?? [];
  const origem = (e: (typeof eventos)[number]): Origem => (e.via === "assistente" ? "assistente" : e.autor ? "pessoa" : "tribunal");
  return (
    <Secao titulo="Linha do tempo">
      {consulta.isPending ? <Esqueleto linhas={4} /> : consulta.isError ? (
        <Falha mensagem="Não foi possível carregar a linha do tempo." aoTentar={() => consulta.refetch()} />
      ) : eventos.length === 0 ? (
        <p className="py-2 text-texto-3">Nada registrado ainda.</p>
      ) : (
        <>
          <LinhaDoTempo eventos={eventos.map((e) => ({
            id: String(e.id), origem: origem(e), quando: diaDoInstante(e.em),
            quem: [e.autor, e.de_outro_processo && `no originário ${e.de_outro_processo}`].filter(Boolean).join(" · ") || null,
            texto: e.texto,
          }))} />
          {consulta.hasNextPage && (
            <div className="pt-3">
              <Botao variante="fantasma" carregando={consulta.isFetchingNextPage} onClick={() => consulta.fetchNextPage()}>Ver mais antigos</Botao>
            </div>
          )}
        </>
      )}
    </Secao>
  );
}
